// React Hooks for Policy Frameworks
// Part of Policy Framework & EU Diseases Implementation

import { useState, useEffect } from 'react';
import policyFrameworkService from '../services/policyFrameworkService';

/**
 * Hook to fetch all policy frameworks
 */
export function usePolicyFrameworks(organizationId, filters = {}) {
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadFrameworks = async () => {
    if (!organizationId) {
      setFrameworks([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await policyFrameworkService.getPolicyFrameworks(
        organizationId,
        filters
      );
      setFrameworks(data);
    } catch (err) {
      console.error('Error loading policy frameworks:', err);
      setError(err.message);
      setFrameworks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFrameworks();
  }, [organizationId, JSON.stringify(filters)]);

  const refresh = () => {
    loadFrameworks();
  };

  return { frameworks, loading, error, refresh };
}

/**
 * Hook to fetch single policy framework
 */
export function usePolicyFramework(frameworkId) {
  const [framework, setFramework] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadFramework = async () => {
    if (!frameworkId) {
      setFramework(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await policyFrameworkService.getPolicyFramework(frameworkId);
      setFramework(data);
    } catch (err) {
      console.error('Error loading policy framework:', err);
      setError(err.message);
      setFramework(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFramework();
  }, [frameworkId]);

  const refresh = () => {
    loadFramework();
  };

  return { framework, loading, error, refresh };
}

/**
 * Hook for policy framework mutations
 */
export function usePolicyFrameworkMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createFramework = async (data, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      const id = await policyFrameworkService.createPolicyFramework(
        data,
        userId,
        userName
      );
      return id;
    } catch (err) {
      console.error('Error creating framework:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateFramework = async (frameworkId, updates, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      await policyFrameworkService.updatePolicyFramework(
        frameworkId,
        updates,
        userId,
        userName
      );
    } catch (err) {
      console.error('Error updating framework:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteFramework = async (frameworkId, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      await policyFrameworkService.deletePolicyFramework(
        frameworkId,
        userId,
        userName
      );
    } catch (err) {
      console.error('Error deleting framework:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    createFramework,
    updateFramework,
    deleteFramework,
    loading,
    error
  };
}

